import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Typography, Chip, IconButton } from "@mui/material";
import { FaCamera } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "app/services/auth-services";
import useUserProfile from "app/hooks/useUserProfile";
import { FaUserCircle } from "react-icons/fa";

const SidebarProfile = () => {
  const { name, role, photo, user } = useUserProfile();

  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const token = localStorage.getItem("accesstoken");

  const header = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  /* ---------- OPEN FILE SELECTOR ---------- */
  const openFilePicker = () => {
    fileRef.current.click();
  };

  /* ---------- IMAGE CHANGE ---------- */
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // preview immediately
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    // convert to base64 (your backend requires)
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];

      try {
        const payload = {
          ...user,
          User_Photo: base64,
          User_Photo_Name: file.name,
          Updated_By: user?.id,
        };

        const res = await axios.put(
          `${BASE_URL}/Felix/Usersignup/`,
          payload,
          header,
        );

        if (res?.data?.id) {
          localStorage.setItem("UserDetails", JSON.stringify(res.data));

          window.dispatchEvent(
            new CustomEvent("profile-updated", { detail: res.data }),
          );
        }
      } catch (err) {
        console.log("Image upload failed", err);
      }
    };
  };

  const getAvatarSrc = () => {
    // 1. Preview after selecting new image
    if (preview) return preview;

    // 2. No photo in DB
    if (!photo || photo === "null" || photo === "undefined") {
      return undefined; // VERY IMPORTANT
    }

    // 3. If backend already sends full URL
    if (
      photo.startsWith("http") ||
      photo.startsWith("blob:") ||
      photo.startsWith("data:")
    ) {
      return photo;
    }

    // 4. Relative path from Django/Node backend
    return `${BASE_URL}${photo}`;
  };

  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const updatedUser = event.detail;

      // clear preview (important)
      setPreview(null);

      // update local storage
      localStorage.setItem("UserDetails", JSON.stringify(updatedUser));

      // FORCE component refresh
      // window.location.reload(); // temporary? NO — we won’t use this
    };

    window.addEventListener("profile-updated", handleProfileUpdate);

    return () => {
      window.removeEventListener("profile-updated", handleProfileUpdate);
    };
  }, []);

  return (
    <Box
      sx={{
        textAlign: "center",
        p: 2,
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      {/* Hidden File Input */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleImageChange}
      />

      {/* Avatar Wrapper */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          mb: 1,
        }}
      >
        {/* Avatar Container (IMPORTANT) */}
        <Box
          sx={{
            position: "relative",
            width: 70,
            height: 70,
            "&:hover .camera-btn": {
              opacity: 1,
              transform: "scale(1)",
            },
            "&:hover .avatar-img": {
              filter: "brightness(0.75)",
            },
          }}
        >
          <Avatar
            className="avatar-img"
            src={getAvatarSrc()}
            alt={name}
            onClick={openFilePicker}
            imgProps={{
              onError: (e) => {
                // if server image missing → hide broken image
                e.target.style.display = "none";
              },
            }}
            sx={{
              width: 68,
              height: 68,
              border: "2px solid #e5e7eb",
              boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
              backgroundColor: "#f3f4f6",
              cursor: "pointer",
              transition: "filter 0.25s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* DEFAULT AVATAR ICON */}
            <FaUserCircle size={68} color="#9ca3af" />
          </Avatar>

          {/* Camera Button perfectly attached to circle */}
          <IconButton
            className="camera-btn"
            onClick={openFilePicker}
            sx={{
              position: "absolute",
              bottom: -4,
              right: -4,
              width: 30,
              height: 30,
              backgroundColor: "#ffffff",
              border: "2px solid #017890",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              color: "#017890",

              /* hidden by default */
              opacity: 0,
              // transform: "scale(0.6)",
              // transition: "all 0.25s ease",

              // "&:hover": {
              //   backgroundColor: "#017890",
              //   color: "#fff",
              // },
            }}
          >
            <FaCamera size={14} />
          </IconButton>
        </Box>

        <Box>
          {/* Name */}
          <Typography
            noWrap
            sx={{
              color: "#111827",
              fontWeight: 600,
              fontSize: "14.5px",
            }}
          >
            {name}
          </Typography>

          {/* Role */}
          <Chip
            label={role}
            size="small"
            sx={{
              mt: 0.6,
              height: 20,
              fontSize: "10.5px",
              fontWeight: 600,
              backgroundColor: "#f3f4f6",
              color: "#374151",
              border: "1px solid #e5e7eb",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SidebarProfile;
